import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useCallback, useState } from 'react';

import { FAVORITES_MODULE_FOR } from '@/modules/blog/constants';
import { addToFavorites } from '@/utils/actions/blog/addToFavorites';
import { removeFavorites } from '@/utils/actions/blog/removeFavorites';

interface FavoriteButtonProps {
  moduleUuid: string;
  isFavorited: boolean;
  setIsFavorited: React.Dispatch<React.SetStateAction<boolean>>;
  setFavoritesCount?: React.Dispatch<React.SetStateAction<number>>;
  favoritesCount?: number;
  variant?: 'full' | 'icon';
  onSuccessToggle?: () => void;
}

export const FavoriteButton = ({
  moduleUuid,
  isFavorited,
  setIsFavorited,
  setFavoritesCount,
  favoritesCount = 0,
  variant = 'full',
  onSuccessToggle,
}: FavoriteButtonProps) => {
  const [localIsFavorited, setLocalIsFavorited] = useState(isFavorited);
  const [localFavoritesCount, setLocalFavoritesCount] = useState(favoritesCount);
  const [error, setError] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState(false);

  const handleFavoriteAction = useCallback(async () => {
    if (isDisabled) return;

    setError(null);
    setIsDisabled(true);

    const newFavoritedState = !localIsFavorited;
    const previousCount = localFavoritesCount;
    const newCount = previousCount + (newFavoritedState ? 1 : -1);

    setLocalIsFavorited(newFavoritedState);
    setIsFavorited(newFavoritedState);

    if (variant === 'full' && setFavoritesCount) {
      setLocalFavoritesCount(newCount);
      setFavoritesCount(newCount);
    }

    if (variant === 'icon' && onSuccessToggle) {
      onSuccessToggle();
    }

    try {
      if (newFavoritedState) {
        await addToFavorites({
          moduleFor: FAVORITES_MODULE_FOR,
          moduleUuid,
        });
      } else {
        await removeFavorites({
          moduleFor: FAVORITES_MODULE_FOR,
          moduleUuid,
        });
      }
    } catch {
      setError('Action failed. Please try again.');
      setLocalIsFavorited(!newFavoritedState);
      setIsFavorited(!newFavoritedState);
      if (variant === 'full' && setFavoritesCount) {
        setLocalFavoritesCount(previousCount);
        setFavoritesCount(previousCount);
      }
    } finally {
      setIsDisabled(false);
    }
  }, [
    isDisabled,
    localIsFavorited,
    localFavoritesCount,
    moduleUuid,
    setIsFavorited,
    setFavoritesCount,
    variant,
  ]);

  return (
    <>
      <button
        onClick={handleFavoriteAction}
        disabled={isDisabled}
        className={clsx(
          'flex items-center justify-center rounded-md transition-all duration-200 gap-1.5',
          {
            'opacity-50 cursor-not-allowed': isDisabled,
            'size-[38px] bg-white border border-gray-300': variant === 'icon',
            'sm:px-3': variant === 'full',
            'hover:bg-gray-100': !isDisabled,
          }
        )}
      >
        <FontAwesomeIcon
          icon={localIsFavorited ? faHeartSolid : faHeartRegular}
          size='lg'
          className={clsx('text-error-500', { 'animate-bounce': isDisabled })}
        />
        {variant === 'full' ? (
          <p className='mr-2 text-sm text-gray-700'>
            {localIsFavorited ? 'Favorited' : 'Add to Favorites'}
          </p>
        ) : null}
      </button>
      {error ? <p className='mt-1 text-sm text-error-500'>{error}</p> : null}
    </>
  );
};
